import axios, { AxiosError } from 'axios';
import type {
  CheckoutSession,
  CheckoutLaunch,
  PaytmJSCheckoutLaunch,
  PayoutSimulatorSession,
  ApiResponse,
} from '../types/checkout';

// Get API base URL from environment variable or default to relative path
// Vite replaces __VITE_API_URL__ during build
declare const __VITE_API_URL__: string;
const API_BASE_URL = typeof __VITE_API_URL__ !== 'undefined' ? __VITE_API_URL__ : '';

type PaytmNotifyHandler = (eventName: string, data: unknown) => void;

interface CheckoutSessionRequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

interface PaytmCheckoutConfig {
  root: string;
  flow: 'DEFAULT';
  data: {
    orderId: string;
    token: string;
    tokenType: 'TXN_TOKEN';
    amount: string;
  };
  handler: {
    notifyMerchant: PaytmNotifyHandler;
  };
}

declare global {
  interface Window {
    Paytm?: {
      CheckoutJS?: {
        onLoad(callback: () => void): void;
        init(config: PaytmCheckoutConfig): Promise<unknown>;
        invoke(): unknown;
      };
    };
  }
}

/**
 * Checkout API Client
 * Handles communication with the hosted checkout backend endpoints
 */
export class CheckoutAPI {
  private baseURL: string;
  private paytmScriptSrc: string | null = null;
  private paytmScriptPromise: Promise<void> | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Fetch checkout session data for a given token
   * GET /checkout/:token
   */
  async getSession(token: string, options: CheckoutSessionRequestOptions = {}): Promise<CheckoutSession> {
    const requestStartedAtMs = Date.now();
    try {
      const response = await axios.get<ApiResponse<CheckoutSession>>(
        `${this.baseURL}/checkout/${token}`,
        {
          timeout: options.timeoutMs ?? 10000,
          ...(options.signal ? { signal: options.signal } : {}),
        },
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch checkout session');
      }

      const session = response.data.data;
      if (!session.crypto_transfer) {
        return session;
      }
      return {
        ...session,
        crypto_transfer: {
          ...session.crypto_transfer,
          client_request_started_at_ms: requestStartedAtMs,
        },
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiResponse<never>>;

        if (axiosError.response?.status === 401) {
          throw new Error('INVALID_TOKEN');
        }
        if (axiosError.response?.status === 404) {
          throw new Error('ORDER_NOT_FOUND');
        }

        throw new Error(
          axiosError.response?.data?.error?.message ||
          axiosError.message ||
          'Failed to fetch checkout session'
        );
      }
      throw error;
    }
  }

  /**
   * Launch checkout - get redirect URL for payment
   * POST /checkout/:token/launch
   */
  async launchCheckout(token: string): Promise<CheckoutLaunch> {
    try {
      const response = await axios.post<ApiResponse<CheckoutLaunch>>(
        `${this.baseURL}/checkout/${token}/launch`
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to launch checkout');
      }

      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiResponse<never>>;

        if (axiosError.response?.status === 401) {
          throw new Error('INVALID_TOKEN');
        }
        if (axiosError.response?.status === 404) {
          throw new Error('ORDER_NOT_FOUND');
        }

        throw new Error(
          axiosError.response?.data?.error?.message ||
          axiosError.message ||
          'Failed to launch checkout'
        );
      }
      throw error;
    }
  }

  /**
   * Simulate a UAT_MANUAL checkout result
   * POST /checkout/:token/simulate
   */
  async simulateUATManualPayment(token: string, status: 'SUCCESS' | 'FAILED'): Promise<CheckoutSession> {
    try {
      const response = await axios.post<ApiResponse<CheckoutSession>>(
        `${this.baseURL}/checkout/${token}/simulate`,
        { status }
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to simulate payment');
      }

      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiResponse<never>>;

        if (axiosError.response?.status === 401) {
          throw new Error('INVALID_TOKEN');
        }
        if (axiosError.response?.status === 404) {
          throw new Error('ORDER_NOT_FOUND');
        }

        throw new Error(
          axiosError.response?.data?.error?.message ||
          axiosError.message ||
          'Failed to simulate payment'
        );
      }
      throw error;
    }
  }

  /**
   * Fetch a UAT_MANUAL payout simulator session
   * GET /payout-simulator/:token
   */
  async getPayoutSimulatorSession(token: string): Promise<PayoutSimulatorSession> {
    try {
      const response = await axios.get<ApiResponse<PayoutSimulatorSession>>(
        `${this.baseURL}/payout-simulator/${token}`
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch payout simulator session');
      }

      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiResponse<never>>;

        if (axiosError.response?.status === 401) {
          throw new Error('INVALID_TOKEN');
        }
        if (axiosError.response?.status === 404) {
          throw new Error('ORDER_NOT_FOUND');
        }

        throw new Error(
          axiosError.response?.data?.error?.message ||
          axiosError.message ||
          'Failed to fetch payout simulator session'
        );
      }
      throw error;
    }
  }

  /**
   * Simulate a UAT_MANUAL payout result
   * POST /payout-simulator/:token/simulate
   */
  async simulateUATManualPayout(token: string, status: 'SUCCESS' | 'FAILED'): Promise<PayoutSimulatorSession> {
    try {
      const response = await axios.post<ApiResponse<PayoutSimulatorSession>>(
        `${this.baseURL}/payout-simulator/${token}/simulate`,
        { status }
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to simulate payout');
      }

      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiResponse<never>>;

        if (axiosError.response?.status === 401) {
          throw new Error('INVALID_TOKEN');
        }
        if (axiosError.response?.status === 404) {
          throw new Error('ORDER_NOT_FOUND');
        }

        throw new Error(
          axiosError.response?.data?.error?.message ||
          axiosError.message ||
          'Failed to simulate payout'
        );
      }
      throw error;
    }
  }

  /**
   * Redirect to the payment channel URL
   */
  redirectToPayment(redirectUrl: string): void {
    window.location.href = redirectUrl;
  }

  /**
   * Submit an HTML form POST to the payment channel.
   *
   * Used by gateways that require per-transaction session params in the
   * request body rather than the URL; a window.location redirect would drop
   * those fields.
   *
   * Field names and values are set via DOM properties (`input.name`,
   * `input.value`) rather than `innerHTML` so attacker-controlled
   * characters cannot break out of the form. The form is removed from
   * the DOM after submission as a defensive cleanup, though the
   * navigation triggered by `form.submit()` already replaces the
   * document. (issue #921)
   */
  submitFormPost(action: string, fields: Record<string, string>): void {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = action;
    form.style.display = 'none';

    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    try {
      form.submit();
    } finally {
      // Defensive cleanup: form.submit() triggers navigation that
      // replaces the document, but if navigation is blocked (test
      // env, browser dialog) the leftover form would otherwise stay
      // attached.
      form.remove();
    }
  }

  /**
   * Invoke Paytm JS Checkout from the platform hosted checkout page.
   *
   * The server only exposes browser-safe session artifacts: MID, orderId,
   * txnToken, amount, and the Paytm host selected for this account. Merchant
   * key/signature material never reaches the browser.
   */
  async invokePaytmJSCheckout(
    launch: PaytmJSCheckoutLaunch | undefined,
    onNotifyMerchant?: PaytmNotifyHandler,
  ): Promise<void> {
    const configData = this.validatePaytmJSCheckoutLaunch(launch);
    const scriptSrc = `${configData.host}/merchantpgpui/checkoutjs/merchants/${encodeURIComponent(configData.mid)}.js`;

    await this.loadPaytmCheckoutScript(scriptSrc);

    const checkout = window.Paytm?.CheckoutJS;
    if (!checkout) {
      throw new Error('Paytm checkout is not available');
    }

    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('Paytm checkout did not load in time')), 15000);

      checkout.onLoad(() => {
        const config: PaytmCheckoutConfig = {
          root: '',
          flow: 'DEFAULT',
          data: {
            orderId: configData.order_id,
            token: configData.txn_token,
            tokenType: 'TXN_TOKEN',
            amount: configData.amount,
          },
          handler: {
            notifyMerchant: (eventName, data) => {
              onNotifyMerchant?.(eventName, data);
            },
          },
        };

        checkout.init(config)
          .then(() => {
            try {
              checkout.invoke();
            } catch (error) {
              window.clearTimeout(timer);
              reject(error instanceof Error ? error : new Error('Paytm checkout failed to open'));
              return;
            }
            window.clearTimeout(timer);
            resolve();
          })
          .catch((error) => {
            window.clearTimeout(timer);
            reject(error instanceof Error ? error : new Error('Paytm checkout failed to initialize'));
          });
      });
    });
  }

  private validatePaytmJSCheckoutLaunch(launch: PaytmJSCheckoutLaunch | undefined): PaytmJSCheckoutLaunch {
    if (!launch) {
      throw new Error('Paytm payment session is not ready');
    }

    const host = launch.host.trim();
    const mid = launch.mid.trim();
    const orderId = launch.order_id.trim();
    const txnToken = launch.txn_token.trim();
    const amount = launch.amount.trim();

    let parsedHost: URL;
    try {
      parsedHost = new URL(host);
    } catch {
      throw new Error('Paytm payment session is not ready');
    }
    const allowedHosts = new Set(['securestage.paytmpayments.com', 'secure.paytmpayments.com']);
    if (
      parsedHost.protocol !== 'https:' ||
      !allowedHosts.has(parsedHost.hostname.toLowerCase()) ||
      (parsedHost.pathname !== '' && parsedHost.pathname !== '/')
    ) {
      throw new Error('Paytm payment session is not ready');
    }

    if (!mid || !orderId || !txnToken || !amount) {
      throw new Error('Paytm payment session is not ready');
    }

    return {
      host: parsedHost.origin,
      mid,
      order_id: orderId,
      txn_token: txnToken,
      amount,
    };
  }

  private loadPaytmCheckoutScript(src: string): Promise<void> {
    if (this.paytmScriptPromise && this.paytmScriptSrc === src) {
      return this.paytmScriptPromise;
    }

    let existing = document.querySelector<HTMLScriptElement>('script[data-tripay-paytm-checkout="true"]');
    if (existing && existing.src !== src) {
      existing.remove();
      existing = null;
      this.paytmScriptPromise = null;
      this.paytmScriptSrc = null;
    }

    if (window.Paytm?.CheckoutJS && existing?.src === src) {
      this.paytmScriptSrc = src;
      this.paytmScriptPromise = Promise.resolve();
      return this.paytmScriptPromise;
    }

    this.paytmScriptSrc = src;
    this.paytmScriptPromise = new Promise<void>((resolve, reject) => {
      const script = existing ?? document.createElement('script');
      script.type = 'application/javascript';
      script.src = src;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.dataset.tripayPaytmCheckout = 'true';
      script.onload = () => resolve();
      script.onerror = () => {
        this.paytmScriptPromise = null;
        this.paytmScriptSrc = null;
        reject(new Error('Failed to load Paytm checkout'));
      };

      if (!existing) {
        document.head.appendChild(script);
      }
    });

    return this.paytmScriptPromise;
  }

  /**
   * Return to merchant using return_url
   */
  returnToMerchant(returnUrl: string, status?: 'success' | 'failure' | 'cancel'): void {
    const url = new URL(returnUrl);
    if (status) {
      url.searchParams.set('status', status);
    }
    window.location.href = url.toString();
  }
}

// Export singleton instance
export const checkoutAPI = new CheckoutAPI();
